import React, { useEffect, useRef, useState } from 'react';
import DashboardLayout from '../../Components/Dashboard/DashboardLayout/DashboardLayout';
import PropTypes from 'prop-types';
import { useFacility } from '../../context/FacilityContext';
import { Alert, Box } from '@mui/material';
import DeviceManagerForm from '../../Components/DeviceManager/DeviceManagerForm/DeviceManagerForm';
import DeviceManagerTable from '../../Components/DeviceManager/DeviceManagerTable/DeviceManagerTable';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import {
  getPossibleResidentsFunction,
  getPossibleRoomsFunction,
} from '../../firebase/functions';
import { db } from '../../firebase/config';

function DeviceManager() {
  const { facilityId } = useFacility();
  const formRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [residents, setResidents] = useState([]);
  const [rooms, setRooms] = useState([]);

  const [infoAlert, setInfoAlert] = useState('');
  const [errorAlert, setErrorAlert] = useState('');
  const resetAlerts = () => {
    setInfoAlert('');
    setErrorAlert('');
  };

  const submitRecord = async (data) => {
    resetAlerts();
    if (data.deviceId.length < 3) {
      setErrorAlert('Device ID must be longer than 2 characters.');
      return;
    }

    data = { ...data, facilityId };

    setIsLoading(true);
    const q = query(
      collection(db, 'devices'),
      where('deviceId', '==', data.deviceId),
    );
    const existingDocs = await getDocs(q);

    if (!existingDocs.empty) {
      setErrorAlert('This device ID is already assigned.');
      setIsLoading(false);
      return;
    }

    try {
      await addDoc(collection(db, 'devices'), data);
      setInfoAlert('Resident assigned to device successfully.');
    } catch (error) {
      setErrorAlert(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteDevice = async (device) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to remove the device with ID ${device.deviceId}?`,
    );

    if (!confirmDelete) {
      return;
    }

    setIsLoading(true);

    try {
      const docRef = doc(db, 'devices', device.id);
      await deleteDoc(docRef);
      setInfoAlert(`Successfully deleted the device.`);
      setRecords((prevRecords) =>
        prevRecords.filter((d) => d.id !== device.id),
      );
    } catch (error) {
      console.error('Error deleting device:', error);
      setErrorAlert(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!facilityId) return;
    setIsLoading(true);

    const fetchDevices = async () => {
      try {
        let conditions = [where('facilityId', '==', facilityId)];
        const q = query(collection(db, 'devices'), ...conditions);

        // Reset the form
        formRef.current.resetForm();

        // Fetch possible residents first
        const result = await getPossibleResidentsFunction({ facilityId });
        const possible = result.data?.residents ?? [];

        // Fetch possible rooms
        const roomsResult = await getPossibleRoomsFunction({ facilityId });
        const possibleRooms = roomsResult.data?.rooms ?? [];

        // Fetch devices data from Firestore
        const querySnapshot = await getDocs(q);
        const recordsArray = querySnapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));

        // Match devices with resident names
        recordsArray.forEach((u) => {
          u.residentName = possible.find(
            (x) => x.id === u.residentId,
          )?.residentName;
        });

        // Match devices with room names
        recordsArray.forEach((u) => {
          u.roomName = possibleRooms.find((x) => x.id === u.roomId)?.roomName;
        });

        setRecords(recordsArray);
        setResidents(possible);
        setRooms(possibleRooms);
      } catch (error) {
        setResidents([]);
        setErrorAlert(error?.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDevices();
  }, [facilityId]);

  return (
    <div>
      <DashboardLayout facilityId={facilityId} isLoading={isLoading}>
        <Box sx={{ marginTop: '15px' }}>
          <h2 style={{ marginBottom: '10px' }}>Add Device</h2>

          {infoAlert && (
            <Alert
              severity="success"
              onClose={() => {
                setInfoAlert('');
              }}
            >
              {infoAlert}
            </Alert>
          )}
          {errorAlert && (
            <Alert
              severity="error"
              onClose={() => {
                setErrorAlert('');
              }}
            >
              {errorAlert}
            </Alert>
          )}

          <Box sx={{ maxWidth: 768, marginBottom: '15px' }}>
            <DeviceManagerForm
              ref={formRef}
              onSubmit={submitRecord}
              residents={residents}
              rooms={rooms}
            />
          </Box>

          <h2 style={{ marginBottom: '24px', marginTop: '48px' }}>
            Current Devices
          </h2>

          <Box>
            <DeviceManagerTable records={records} onDelete={deleteDevice} />
          </Box>
        </Box>
      </DashboardLayout>
    </div>
  );
}

DeviceManager.propTypes = {};

export default DeviceManager;
